(() => {
  'use strict';
  const script = document.querySelector('script[data-rof-posthog]');
  if (!script || window.rofPosthogInstalled || window.Shopify?.designMode) return;
  if (!['raisedonfilm.com', 'www.raisedonfilm.com'].includes(location.hostname)) return;
  // A gift card's link is its code, so its page sends nothing either.
  if (/\/(account|checkouts?|orders|gift_cards)(\/|$)/.test(location.pathname)) return;
  if (new URLSearchParams(location.search).has('preview_theme_id') || window.Shopify?.previewMode || /SH1-QA/i.test(navigator.userAgent)) return;
  window.rofPosthogInstalled = true;
  let started = false;
  let ready = false;
  let client;
  let active = false;
  let viewed = false;
  const metadata = {
    release: 'raisedonfilm@code-9ca9f79e628af2e853bb24da2cb06cdde9c6cc1f9e4a925e332ed8697d2ff849',
    environment: window.Shopify?.theme?.role === 'main' ? 'production' : 'preview',
    service: 'storefront',
  };
  const data = script.dataset;
  const defined = (object) => Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined && value !== '' && value !== null));
  const number = (value, divisor = 1) => (value === undefined || value === '' || !Number.isFinite(Number(value)) ? undefined : Number(value) / divisor);
  const flag = (value) => (value === 'true' ? true : value === 'false' ? false : undefined);
  // Search text can contain contact details typed by mistake; keep the words, never the details.
  const searchText = (value) => String(value || '').replace(/\S+@\S+/g, '[redacted]').replace(/\+?[\d\s()-]{7,}/g, '[redacted]').trim().toLowerCase().slice(0, 150);
  // A hashed email is the shared identity, so one shopper's phone, laptop and checkout join one person.
  // The address itself never leaves the browser.
  async function emailIdentity(email) {
    const normalized = String(email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+$/.test(normalized) || !window.crypto?.subtle) return undefined;
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    return `email_sha256:${[...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('')}`;
  }
  async function identifyEmail(email) {
    try {
      const id = await emailIdentity(email);
      // Only an anonymous browser joins an email; a device already tied to someone stays theirs.
      if (id && active && allowed() && (client.get_property('$user_state') || 'anonymous') === 'anonymous') client.identify(id);
    } catch { /* Linking is best effort and must never interrupt shopping. */ }
  }
  // Sign-up forms queue the address they gave Klaviyo; drained once analytics is active.
  function linkVisitorEmails() {
    const emails = Array.isArray(window.rofVisitorEmails) ? window.rofVisitorEmails : [];
    window.rofVisitorEmails = [];
    emails.forEach(identifyEmail);
  }

  // Every event from this document carries where it happened, so any event can be split by page.
  const page = defined({
    page_type: data.pageType,
    template: data.template,
    product_handle: data.productHandle,
    product_id: data.productId,
    collection_handle: data.collectionHandle,
  });
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  // Keep failure capture independent of SDK download timing, but never retain denied events.
  let pendingErrors = [];
  const privatePath = /(\/(?:checkouts?|orders|account|gift_cards)(?:\/|$)).*/i;
  const cleanErrorUrl = (value) => value.split(/[?#]/)[0]
    .replace(/(https?:\/\/)[^/\s]*@/gi, '$1')
    .replace(privatePath, '$1[redacted]');
  function scrub(value, depth = 0) {
    if (depth > 12) return '[truncated]';
    if (typeof value === 'string') return value.replace(/https?:\/\/[^\s"'<>]+/g, cleanErrorUrl);
    if (!value || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(item => scrub(item, depth + 1));
    const result = {};
    for (const [key, item] of Object.entries(value)) {
      if (/^(?:headers|cookies|body|request_body|response_body|query_string|query|fragment)$/i.test(key)) continue;
      result[key] = typeof item === 'string' && /(?:url|href|filename|abs_path|referrer|pathname)$/i.test(key)
        ? cleanErrorUrl(item) : scrub(item, depth + 1);
    }
    return result;
  }
  function isBrowserBridgeNoise(error) {
    const message = typeof error?.message === 'string' ? error.message : '';
    const stack = typeof error?.stack === 'string' ? error.stack : '';
    const mobileWebkit = /iPhone|iPad|iPod/.test(navigator.userAgent);
    return (mobileWebkit && error?.name === 'ReferenceError' && message === "Can't find variable: _AutofillCallbackHandler") ||
      (mobileWebkit && error?.name === 'TypeError' && message === "undefined is not an object (evaluating 'window.webkit.messageHandlers')") ||
      (message === 'Error invoking postMessage: Java object is gone' && /iabjs:\/\/|sendDataToNative|sendBeforeUnloadMessage/.test(stack));
  }
  window.rofReportError = (error, properties = {}) => {
    if (!allowed() || isBrowserBridgeNoise(error)) return;
    if (!ready) {
      if (pendingErrors.length < 10) pendingErrors.push([error, properties]);
      return;
    }
    try { client.captureException(error, { ...metadata, ...properties }); }
    catch { /* Reporting must never interrupt shopping. */ }
  };
  // Explicit listeners also cover the period while the analytics SDK is loading.
  window.addEventListener('error', event => {
    if (event.error) window.rofReportError(event.error, { $exception_handled: false });
  });
  window.addEventListener('unhandledrejection', event => {
    window.rofReportError(event.reason, { $exception_handled: false });
  });

  function syncConsent() {
    if (!allowed()) {
      active = false;
      pendingErrors = [];
      pendingEvents = [];
      if (ready) {
        client.stopSessionRecording();
        client.opt_out_capturing();
      }
      return;
    }
    if (!started) {
      started = true;
      install();
    } else if (ready && !active) {
      activate();
    }
  }

  function pageviewProperties() {
    return defined({
      product_available: flag(data.productAvailable),
      product_price: number(data.productPrice, 100),
      product_type: data.productType,
      collection_products_count: number(data.collectionCount),
      search_term: data.pageType === 'search' ? searchText(data.searchTerms) : undefined,
      search_results_count: data.pageType === 'search' ? number(data.searchCount) : undefined,
      cart_item_count: number(data.cartCount),
      cart_value: number(data.cartValue, 100),
      logged_in: Boolean(data.customerId),
    });
  }

  function capturePageview() {
    client.capture('$pageview', pageviewProperties(), {
      $set: defined({ last_market: data.market, last_currency: data.currency }),
      $set_once: defined({ first_market: data.market, first_page_type: data.pageType }),
    });
  }

  function activate() {
    if (!allowed()) return syncConsent();
    active = true;
    const ph = client;
    const customerId = data.customerId;
    const previousCustomer = ph.get_property('shopify_customer_id');
    if (previousCustomer && previousCustomer !== customerId) ph.reset();
    ph.opt_in_capturing({ captureEventName: false });
    ph.register({ schema_version: 1, source: 'storefront_theme', market: data.market, currency: data.currency });
    if (customerId) {
      const customerKey = `shopify_customer:${customerId}`;
      const previousId = ph.get_distinct_id?.();
      ph.identify(data.customerEmailHash ? `email_sha256:${data.customerEmailHash}` : customerKey, defined({
        shopify_customer_id: customerId,
        orders_count: number(data.customerOrders),
        total_spent: number(data.customerSpent, 100),
        accepts_marketing: flag(data.customerAcceptsMarketing),
        customer_tags: data.customerTags ? data.customerTags.split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 30) : undefined,
      }));
      // Sign-ins recorded before email linking move onto the email identity once.
      if (data.customerEmailHash && previousId === customerKey) ph.capture('$merge_dangerously', { alias: customerKey });
      ph.register({ shopify_customer_id: customerId });
    }
    window.rofAnalytics = { capture, markInternal: () => ph.setInternalOrTestUser() };

    client.startSessionRecording();
    const buffered = pendingErrors;
    pendingErrors = [];
    buffered.forEach(([error, properties]) => window.rofReportError(error, properties));
    if (!viewed) {
      viewed = true;
      capturePageview();
      observeProducts();
      afterRedirect();
      marketChange();
    }
    linkVisitorEmails();
    const queued = pendingEvents;
    pendingEvents = [];
    queued.forEach(([name, properties, options]) => client.capture(name, properties, options));
    publishIdentity();
  }

  // Use the initialized client: window.posthog can still be the snippet queue in loaded().
  let identityTimer;
  function publishIdentity(attempt = 0) {
    clearTimeout(identityTimer);
    if (!active || !allowed()) return;
    const distinctId = client.get_distinct_id();
    const sessionId = client.get_session_id();
    const publish = window.Shopify?.analytics?.publish;
    const retry = () => {
      if (attempt < 10 && active && allowed()) identityTimer = setTimeout(() => publishIdentity(attempt + 1), 200);
    };
    if (!publish || !distinctId || !sessionId) return retry();
    try {
      Promise.resolve(publish('rof_identity_ready', { distinct_id: distinctId, session_id: sessionId }))
        .then(result => { if (result === false) retry(); }, retry);
    } catch { retry(); }
  }

  function install() {
    // Official PostHog queue bootstrap; the SDK is fetched only after Shopify allows analytics.
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split('.');2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement('script')).type='text/javascript',p.crossOrigin='anonymous',p.async=!0,p.src=s.api_host.replace('.i.posthog.com','-assets.i.posthog.com')+'/static/array.js',(r=t.getElementsByTagName('script')[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a='posthog',u.people=u.people||[],Object.defineProperty(u,'toString',{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e='posthog';return'posthog'!==a&&(e+='.'+a),t||(e+=' (stub)'),e}}),Object.defineProperty(u.people,'toString',{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+'.people (stub)'}}),o='init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug'.split(' '),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    const region = data.region === 'eu' ? 'eu' : 'us';
    window.posthog.init(data.token, {
      // US traffic goes through ROF's own subdomain (PostHog's managed proxy), so ad blockers don't drop it.
      api_host: region === 'us' ? 'https://r.raisedonfilm.com' : `https://${region}.i.posthog.com`,
      ui_host: `https://${region}.posthog.com`,
      defaults: '2026-05-30',
      // Every visitor gets a profile, so first-touch source and later purchases stay on one person.
      person_profiles: 'always',
      capture_pageview: false,
      capture_pageleave: false,
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      disable_session_recording: true,
      // Clicks keep their visible label and link so each one names what was clicked. Field values are never captured.
      autocapture: { capture_copied_text: true },
      capture_dead_clicks: true,
      enable_heatmaps: true,
      enable_recording_console_log: true,
      capture_performance: { web_vitals: true, network_timing: true },
      // Our early listeners own global capture, avoiding duplicate SDK reports.
      capture_exceptions: false,
      disable_surveys: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[contenteditable], .ph-mask, [data-private]',
        blockSelector: 'iframe, [data-posthog-block]',
        recordHeaders: false,
        recordBody: false,
        maskCapturedNetworkRequestFn: request => scrub(request),
      },
      before_send: (event) => {
        if (!event || !allowed()) return null;
        if (!/^(?:\$snapshot|\$\$)/.test(event.event)) event.properties = { ...page, ...event.properties };
        // Preserve marketing attribution on analytics events; error payloads never carry queries.
        if (event.event === '$exception') {
          event.properties = { ...scrub(event.properties), ...metadata };
          delete event.$set;
          delete event.$set_once;
        }
        // Every link the SDK adds (this page, its referrer, the first visit's, the session's first), on the event and
        // on the person alike.
        const clean = event.event === '$exception' ? cleanErrorUrl : cleanUrl;
        event.properties = cleanLinks(event.properties, clean);
        for (const key of ['$set', '$set_once']) {
          if (event[key]) event[key] = cleanLinks(event[key], clean);
          if (event.properties?.[key]) event.properties[key] = cleanLinks(event.properties[key], clean);
        }
        return event;
      },
      loaded: (posthog) => {
        client = posthog;
        ready = true;
        syncConsent();
        client.onSessionId(() => {
          clearTimeout(identityTimer);
          identityTimer = setTimeout(() => publishIdentity(), 0);
        });
      },
    });
  }

  // A link keeps only the kind of a private link; a value that isn't a link (the SDK's '$direct') stays as it is.
  function cleanLinks(fields, clean) {
    if (!fields || typeof fields !== 'object') return fields;
    const result = { ...fields };
    for (const [key, value] of Object.entries(result)) {
      if (typeof value !== 'string') continue;
      if (/(?:url|href|referrer)$/i.test(key) && /^https?:\/\//i.test(value)) result[key] = clean(value);
      else if (/pathname$/i.test(key)) result[key] = value.replace(privatePath, '$1[redacted]');
    }
    return result;
  }

  function cleanUrl(value) {
    try {
      const url = new URL(value, location.origin);
      const keep = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'variant', 'sort_by', 'page'];
      for (const key of [...url.searchParams.keys()]) if (!keep.includes(key)) url.searchParams.delete(key);
      url.hash = '';
      // A gift card, order, checkout or account link can come back as the next page's referrer.
      url.pathname = url.pathname.replace(privatePath, '$1[redacted]');
      return url.href;
    } catch { return ''; }
  }

  // Interactions before the SDK finishes loading are kept briefly, only while analytics is allowed.
  let pendingEvents = [];
  function capture(name, properties = {}, options = {}) {
    if (!allowed()) return;
    if (active) client.capture(name, properties, options);
    else if (pendingEvents.length < 50) pendingEvents.push([name, properties, { ...options, timestamp: new Date() }]);
  }

  const sectionName = (node) => (node?.id || node?.localName || '').replace(/^shopify-section-/, '').replace(/template--\d+__/, '').replace(/\d+/g, '').replace(/[-_]{2,}/g, '_').replace(/^[-_]+|[-_]+$/g, '').slice(0, 60) || undefined;
  function context(element) {
    const card = element?.closest?.('product-card');
    return defined({
      product_handle: card?.getAttribute('handle') || data.productHandle || undefined,
      placement: element?.closest?.('.drawer--look') ? 'shop_the_look' :
        element?.closest?.('cart-drawer') ? 'cart' :
        element?.closest?.('search-drawer') ? 'search' :
        element?.closest?.('product-recommendations') ? 'recommendations' : data.pageType,
    });
  }
  // Grid position and list show which slots and sections earn attention and clicks.
  function cardContext(card) {
    const list = card?.closest?.('.shopify-section, cart-drawer, search-drawer');
    const cards = list ? [...Element.prototype.querySelectorAll.call(list, 'product-card')] : [];
    return defined({
      ...context(card),
      position: cards.includes(card) ? cards.indexOf(card) + 1 : undefined,
      list: sectionName(list),
      search_term: card?.closest?.('search-drawer') ? lastSearch || undefined : undefined,
    });
  }

  const seenCards = new WeakSet();
  const cardObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting && active && allowed()) {
        capture('product_impression', cardContext(entry.target));
        cardObserver.unobserve(entry.target);
      }
    }
  }, { threshold: 0.5 });
  function observeProducts() {
    document.querySelectorAll('product-card').forEach(card => {
      if (!seenCards.has(card)) { seenCards.add(card); cardObserver.observe(card); }
    });
  }
  document.addEventListener('cart-drawer:refreshed', observeProducts);
  document.addEventListener('product:rerender', observeProducts);
  new MutationObserver(records => {
    if (active && records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (Element.prototype.matches.call(node, 'product-card') || Element.prototype.querySelector.call(node, 'product-card'))))) observeProducts();
  }).observe(document.body, { childList: true, subtree: true });
  // Checkout leaves the storefront at once, before batched clicks are sent, and Shop Pay's own
  // checkout never reports a start. The tap is recorded here and sent before the page unloads.
  const expressCheckout = '.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .additional-checkout-buttons';
  function checkoutClicked(control) {
    const lines = [...(control.closest('cart-drawer, .shopify-section')?.querySelectorAll('line-item[data-variant-id]') || [])];
    const handles = lines.map(line => line.querySelector('a[href*="/products/"]')?.pathname.split('/products/')[1]).filter(Boolean);
    capture('checkout_clicked', defined({
      placement: context(control).placement,
      checkout_type: control.matches(expressCheckout) ? 'express' : 'checkout',
      product_handles: handles.length ? [...new Set(handles)] : data.productHandle ? [data.productHandle] : undefined,
      variant_ids: lines.length ? lines.map(line => line.dataset.variantId) : undefined,
    }), { transport: 'sendBeacon', send_instantly: true });
  }
  let lastFormClick = { at: 0 };
  document.addEventListener('click', event => {
    const link = event.target.closest?.('product-card a[href*="/products/"]');
    if (link) capture('product_clicked', cardContext(link.closest('product-card')));
    const control = event.target.closest?.('[aria-controls]');
    if (control?.getAttribute('aria-controls')?.includes('size-chart')) capture('size_guide_opened', context(control));
    const checkout = event.target.closest?.(`[name="checkout"], ${expressCheckout}`);
    if (checkout) checkoutClicked(checkout);
    const formControl = event.target.closest?.('[class*="kl-teaser-"], [class*="kl-private"] button');
    if (formControl) lastFormClick = { at: Date.now(), via: formControl.matches('.klaviyo-close-form') ? 'close_button' : formControl.matches('[class*="kl-teaser-"]') ? 'teaser' : (formControl.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40) || 'button' };
  });
  document.addEventListener('variant:change', event => {
    const variant = event.detail?.variant;
    if (variant) capture('variant_selected', { ...context(event.target), variant_id: String(variant.id), variant_title: variant.public_title || variant.title, available: variant.available, price: variant.price / 100 });
  });
  document.addEventListener('cart:error', event => capture('cart_error', { ...context(event.target), error_type: 'cart_request_failed' }));
  document.addEventListener('variant:add', event => capture('merchandising_add_succeeded', { ...context(event.target), variant_ids: event.detail?.items?.map(item => String(item.variant_id || item.id)) }));
  document.addEventListener('facet:update', event => {
    const url = new URL(event.detail.url, location.origin);
    capture('collection_filters_changed', { sort_by: url.searchParams.get('sort_by'), filters: [...url.searchParams].filter(([key]) => key.startsWith('filter.')).map(([key, value]) => ({ key, value })) });
  });
  document.addEventListener('media:play', event => capture('product_media_played', context(event.target)));
  document.addEventListener('lightbox:open', event => capture('product_media_zoomed', { ...context(event.target), media_index: event.detail?.index }));
  document.addEventListener('accordion:after-open', event => capture('product_information_opened', { ...context(event.target), section: event.target.querySelector('summary')?.textContent?.trim().slice(0, 100) }));
  document.addEventListener('rof:restock-subscribed', event => capture('back_in_stock_subscribed', { ...context(event.target), variant_ids: event.detail?.variantIds }, { $set: { back_in_stock_subscriber: true } }));
  document.addEventListener('rof:launch-signup', event => capture('coming_soon_signed_up', { ...context(event.target), requested_product: event.detail?.productHandle || undefined, requested_size: event.detail?.size || undefined }, { $set: { newsletter_subscriber: true } }));

  // Drawers, popovers and modals: cart, search, menu, quick buy, filters, size guide, country.
  const openedAt = new WeakMap();
  document.addEventListener('dialog:after-show', event => {
    const dialog = event.target;
    if (!dialog?.localName) return;
    openedAt.set(dialog, Date.now());
    capture('dialog_opened', { ...context(event.detail?.activator || dialog), dialog: sectionName(dialog), dialog_type: dialog.localName, opened_by: event.detail?.activator ? 'shopper' : 'storefront' });
  }, true);
  document.addEventListener('dialog:before-hide', event => {
    const dialog = event.target;
    if (!dialog?.localName || !openedAt.has(dialog)) return;
    const seconds = Math.round((Date.now() - openedAt.get(dialog)) / 100) / 10;
    openedAt.delete(dialog);
    capture('dialog_closed', { ...context(dialog), dialog: sectionName(dialog), dialog_type: dialog.localName, open_seconds: seconds });
  }, true);

  // What shoppers type into search, after they pause; Shopify reports only submitted searches.
  let searchTimer;
  let lastSearch = '';
  document.addEventListener('input', event => {
    const input = event.target;
    if (!input?.matches?.('input[name="q"], input[type="search"]')) return;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      const term = searchText(input.value);
      if (term.length < 2 || term === lastSearch) return;
      lastSearch = term;
      const drawer = input.closest?.('search-drawer');
      capture('search_typed', defined({ search_term: term, results_visible: drawer ? drawer.querySelectorAll('product-card').length : undefined, placement: drawer ? 'search_drawer' : data.pageType }));
    }, 1200);
  });

  document.addEventListener('cart:change', event => {
    const cart = event.detail?.cart;
    if (!cart || typeof cart !== 'object') return;
    capture('cart_updated', defined({ change: event.detail.baseEvent, cart_item_count: cart.item_count, cart_value: number(cart.total_price, 100), cart_discount: number(cart.total_discount, 100), cart_currency: cart.currency }),
      /^variant:add/.test(event.detail.baseEvent) ? { $set: { has_added_to_cart: true } } : {});
  });
  document.addEventListener('cart:discount:error', event => capture('cart_discount_failed', context(event.target)));
  document.addEventListener('gift-message:saved', event => {
    const message = String(event.detail?.message || '');
    capture('gift_message_saved', { has_message: message.trim().length > 0, message_length: message.length });
  });
  // Messages the storefront shows a shopper, such as stock limits or request failures.
  document.addEventListener('toast:show', event => capture('storefront_message_shown', defined({ tone: event.detail?.tone, message: String(event.detail?.message || '').slice(0, 150) })));

  // Newsletter and contact forms reload the page; Shopify marks a successful post in the address.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form?.querySelector?.('input[name="contact[tags]"][value*="newsletter"]')) return;
    capture('newsletter_form_submitted', { ...context(form), form: sectionName(form) });
    // Kept only until the reload shows whether Shopify accepted the address.
    try { if (allowed()) sessionStorage.setItem('rof:ph-newsletter-email', form.querySelector('input[name="contact[email]"]')?.value || ''); } catch {}
  }, true);
  document.addEventListener('rof:visitor-email', () => { if (active) linkVisitorEmails(); });
  function afterRedirect() {
    const params = new URLSearchParams(location.search);
    let email = '';
    try { email = sessionStorage.getItem('rof:ph-newsletter-email') || ''; sessionStorage.removeItem('rof:ph-newsletter-email'); } catch {}
    if (params.get('customer_posted') === 'true') {
      capture('newsletter_subscribed', { form: sectionName({ id: location.hash.slice(1) }) }, { $set: { newsletter_subscriber: true } });
      if (email) identifyEmail(email);
    }
    if (params.get('contact_posted') === 'true') capture('contact_form_submitted', { form: sectionName({ id: location.hash.slice(1) }) });
  }
  // Country or currency changes reload the storefront; compare with the previous page.
  function marketChange() {
    try {
      const previous = sessionStorage.getItem('rof:ph-market');
      if (data.market) sessionStorage.setItem('rof:ph-market', data.market);
      if (previous && data.market && previous !== data.market) capture('market_changed', { from_market: previous, to_market: data.market, currency: data.currency });
    } catch { /* Storage can be unavailable in private browsing. */ }
  }

  // Klaviyo stores the form version each visitor qualified for (their A/B test variation, chosen before any delay
  // or page-count rule) or was shown. Each goes on the person once and on every later event as
  // klaviyo_form_<formId> = version, so a test can be read per assigned visitor rather than per view.
  const formVersions = {};
  function storedFormVersions() {
    try {
      const stored = JSON.parse(localStorage.getItem('klaviyoOnsite') || 'null')?.viewedForms?.modal?.viewedForms;
      return Object.fromEntries(Object.entries(stored && typeof stored === 'object' ? stored : {})
        .map(([form, version]) => [form, String(version)]).filter(([form, version]) => /^[A-Za-z0-9]{6}$/.test(form) && /^\d{1,12}$/.test(version)));
    } catch { return {}; }
  }
  function recordFormVersions() {
    if (!active) return;
    const fresh = Object.entries(storedFormVersions()).filter(([form, version]) => formVersions[form] !== version);
    if (!fresh.length) return;
    let sent = {};
    try { sent = JSON.parse(localStorage.getItem('rof:ph-klaviyo-forms') || '{}') || {}; } catch { /* Storage can be unavailable. */ }
    const properties = Object.fromEntries(fresh.map(([form, version]) => [`klaviyo_form_${form}`, version]));
    fresh.forEach(([form, version]) => { formVersions[form] = version; });
    client.register(properties);
    fresh.filter(([form, version]) => sent[form] !== version).forEach(([form, version]) => {
      capture('klaviyo_form_assigned', { form_id: form, form_version_id: version }, { $set_once: { [`klaviyo_form_${form}`]: version } });
      sent[form] = version;
    });
    try { localStorage.setItem('rof:ph-klaviyo-forms', JSON.stringify(sent)); } catch { /* Storage can be unavailable. */ }
  }
  [2000, 6000, 15000].forEach(delay => setTimeout(recordFormVersions, delay));

  // Klaviyo pop-up and embedded forms announce their lifecycle on window; contact details stay out.
  // Steps are numbered from each open, so a close shows the step the visitor left on. The click
  // just before an open or close says whether it came from the corner box, the close button or a
  // form button such as "No thanks".
  const klaviyoEvents = { open: 'klaviyo_form_viewed', embedOpen: 'klaviyo_form_viewed', viewedStep: 'klaviyo_form_step_viewed', close: 'klaviyo_form_closed', closeTeaser: 'klaviyo_form_teaser_closed', submit: 'klaviyo_form_submitted', stepSubmit: 'klaviyo_form_step_submitted', redirectedToUrl: 'klaviyo_form_redirected' };
  const formSteps = {};
  window.addEventListener('klaviyoForms', event => {
    const detail = event.detail || {};
    if (typeof detail.type !== 'string') return;
    const name = klaviyoEvents[detail.type] || 'klaviyo_form_other';
    recordFormVersions();
    const formId = typeof detail.formId === 'string' ? detail.formId : undefined;
    const version = /^\d{1,12}$/.test(String(detail.formVersionId ?? '')) ? String(detail.formVersionId) : formVersions[formId];
    const steps = formSteps[formId] ||= { number: 0 };
    if (detail.type === 'open' || detail.type === 'embedOpen') Object.assign(steps, { number: 0, submitted: undefined });
    if (detail.type === 'viewedStep') steps.number++;
    const stepName = typeof detail.metaData?.$step_name === 'string' ? detail.metaData.$step_name.slice(0, 60) : undefined;
    if (stepName) steps.submitted = stepName;
    const clicked = Date.now() - lastFormClick.at < 2000 ? lastFormClick.via : undefined;
    capture(name, defined({
      form_id: formId,
      form_version_id: version,
      form_display: detail.type === 'embedOpen' ? 'embedded' : detail.type === 'open' ? 'popup' : undefined,
      opened_from: detail.type === 'open' ? (clicked === 'teaser' ? 'teaser' : 'auto') : undefined,
      step_number: detail.type === 'closeTeaser' ? undefined : steps.number || undefined,
      step_name: stepName,
      last_step_submitted: detail.type === 'close' ? steps.submitted : undefined,
      closed_with: detail.type === 'close' ? clicked || 'other' : undefined,
      klaviyo_event: name === 'klaviyo_form_other' ? detail.type.slice(0, 40) : undefined,
    }), detail.type === 'submit' ? { $set: { newsletter_subscriber: true } } : {});
    if (detail.type === 'submit') identifyEmail(detail.metaData?.$email);
  });

  // How far shoppers browse the product gallery, reported with the page leave.
  const galleryIndexes = new Set();
  document.addEventListener('carousel:change', event => {
    if (event.target?.closest?.('product-gallery') && Number.isInteger(event.detail?.index)) galleryIndexes.add(event.detail.index);
  }, true);

  // Unlike GA4's 90% scroll event, this marks the first meaningful movement.
  let scrolled = false;
  let maxScrollY = 0;
  window.addEventListener('scroll', () => {
    if (!active || !allowed()) return;
    maxScrollY = Math.max(maxScrollY, window.scrollY);
    if (scrolled || window.scrollY < 8) return;
    scrolled = true;
    client.capture('storefront_scrolled', { scroll_y: Math.round(window.scrollY) });
  }, { passive: true });
  window.addEventListener('pagehide', () => {
    if (!active || !allowed() || !viewed) return;
    client.capture('$pageleave', defined({
      scrolled,
      max_scroll_y: Math.round(maxScrollY),
      gallery_images_viewed: data.pageType === 'product' ? new Set([0, ...galleryIndexes]).size : undefined,
    }), { transport: 'sendBeacon', send_instantly: true });
  });
  // Back and forward navigation can restore a page from memory without running scripts again.
  window.addEventListener('pageshow', event => {
    if (!event.persisted || !active || !allowed()) return;
    scrolled = false;
    maxScrollY = window.scrollY;
    galleryIndexes.clear();
    capturePageview();
  });
  document.addEventListener('visitorConsentCollected', syncConsent);
  if (window.Shopify?.customerPrivacy) syncConsent();
  else if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) syncConsent();
    });
  }
})();
